<template>
  <HrPageLayout
    title="Employee Lifecycle"
    subtitle="Recruitment through hire, career moves, and separation — one timeline"
    icon="🔄"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'HR Management' },
      { label: 'Employee Lifecycle' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-secondary" @click="loadDashboard">
        <i class="bx bx-refresh"></i> Refresh
      </button>
    </template>

    <div class="hr-metrics-grid hr-mb-4">
      <div class="hr-metric-card gradient-1">
        <div class="hr-metric-content">
              <p class="hr-metric-label">Active employees</p>
        <h2 class="hr-metric-value">{{ dashboard.activeEmployees }}</h2>
            </div>
      </div>
      <div class="hr-metric-card gradient-2">
        <div class="hr-metric-content">
              <p class="hr-metric-label">In recruitment</p>
        <h2 class="hr-metric-value">{{ dashboard.inRecruitmentPipeline }}</h2>
            </div>
      </div>
      <div class="hr-metric-card gradient-3">
        <div class="hr-metric-content">
              <p class="hr-metric-label">Onboarding in progress</p>
        <h2 class="hr-metric-value">{{ dashboard.onboardingInProgress }}</h2>
            </div>
      </div>
      <div class="hr-metric-card gradient-4">
        <div class="hr-metric-content">
              <p class="hr-metric-label">Pending promotions</p>
        <h2 class="hr-metric-value">{{ dashboard.pendingPromotions }}</h2>
            </div>
      </div>
      <div class="hr-metric-card gradient-2">
        <div class="hr-metric-content">
              <p class="hr-metric-label">Pending transfers</p>
        <h2 class="hr-metric-value">{{ dashboard.pendingTransfers }}</h2>
            </div>
      </div>
      <div class="hr-metric-card gradient-3">
        <div class="hr-metric-content">
              <p class="hr-metric-label">Pending terminations</p>
        <h2 class="hr-metric-value">{{ dashboard.pendingTerminations }}</h2>
            </div>
      </div>
    </div>

    <div class="row g-4">
      <div class="col-lg-5">
        <div class="hr-chart-card">
          <div class="hr-chart-body">
            <h5 class="hr-mb-3">Find employee timeline</h5>
            <input
              v-model="searchQuery"
              type="text"
              class="hr-input hr-mb-2"
              placeholder="Name, emp no, email, ID…"
              @input="debouncedSearch"
            />
            <ul v-if="searchResults.length" class="lifecycle-search-list">
              <li
                v-for="e in searchResults"
                :key="e.id"
                :class="{ active: selectedEmployee && selectedEmployee.id === e.id }"
                @click="selectEmployee(e)"
              >
                <strong>{{ e.firstName }} {{ e.lastName }}</strong>
                <small class="d-block text-muted">{{ e.empNo }} · {{ e.departmentName }}</small>
              </li>
            </ul>
            <p v-else-if="searchQuery.length >= 2" class="text-muted">No matches.</p>

            <h5 class="hr-mt-4 hr-mb-2">Recruitment pipeline (not yet hired)</h5>
            <ul class="lifecycle-search-list compact">
              <li
                v-for="a in pipeline"
                :key="'p-' + a.id"
                @click="selectApplicant(a)"
              >
                <strong>{{ a.applicant_name }}</strong>
                <small class="d-block text-muted">{{ a.vacancy_title }} · {{ a.status }}</small>
              </li>
              <li v-if="!pipeline.length" class="text-muted">No open applications.</li>
            </ul>
          </div>
        </div>

        <div class="hr-chart-card hr-mt-3">
          <div class="hr-chart-body">
            <h5 class="hr-mb-2">Quick actions</h5>
            <router-link to="/recruitment" class="hr-btn hr-btn-sm hr-btn-outline hr-me-2 hr-mb-2">Recruitment</router-link>
            <router-link to="/hr-promotions" class="hr-btn hr-btn-sm hr-btn-outline hr-me-2 hr-mb-2">Promotions</router-link>
            <router-link to="/hr-transfers" class="hr-btn hr-btn-sm hr-btn-outline hr-me-2 hr-mb-2">Transfers</router-link>
            <router-link to="/separation-termination" class="hr-btn hr-btn-sm hr-btn-outline hr-mb-2">Separation</router-link>
          </div>
        </div>
      </div>

      <div class="col-lg-7">
        <div class="hr-chart-card">
          <div class="hr-chart-body">
            <div v-if="selectedEmployee" class="hr-mb-3">
              <h4>{{ selectedEmployee.firstName }} {{ selectedEmployee.lastName }}</h4>
              <p class="text-muted mb-0">
                {{ selectedEmployee.empNo }} · {{ selectedEmployee.departmentName }} ·
                <span class="badge bg-secondary">{{ selectedEmployee.status }}</span>
              </p>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-link p-0 hr-mt-1" @click="openEmployeeProfile">
                Open employee profile
              </button>
            </div>
            <div v-else-if="selectedApplicant" class="hr-mb-3">
              <h4>{{ selectedApplicant.applicant_name }}</h4>
              <p class="text-muted">{{ selectedApplicant.vacancy_title }} — {{ selectedApplicant.status }}</p>
              <router-link :to="{ name: 'recruitment' }" class="hr-btn hr-btn-sm hr-btn-primary">Open recruitment</router-link>
            </div>
            <p v-else class="text-muted">Select an employee or applicant to view their lifecycle timeline.</p>

            <EmployeeLifecycleTimeline
              :employee-id="timelineEmployeeId"
              :application-id="timelineApplicationId"
            />
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import EmployeeLifecycleTimeline from "@/components/hrActions/EmployeeLifecycleTimeline.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeLifecycle",
  components: { HrPageLayout, EmployeeLifecycleTimeline },
  data() {
    return {
      dashboard: {
        activeEmployees: 0,
        inRecruitmentPipeline: 0,
        onboardingInProgress: 0,
        pendingPromotions: 0,
        pendingTransfers: 0,
        pendingTerminations: 0
      },
      searchQuery: "",
      searchResults: [],
      pipeline: [],
      selectedEmployee: null,
      selectedApplicant: null,
      searchTimer: null
    };
  },
  computed: {
    timelineEmployeeId() {
      return this.selectedEmployee ? this.selectedEmployee.id : 0;
    },
    timelineApplicationId() {
      if (this.selectedApplicant) return this.selectedApplicant.id;
      return 0;
    }
  },
  mounted() {
    this.loadDashboard();
    this.loadPipeline();
  },
  methods: {
    loadDashboard() {
      const data = new FormData();
      data.append("function", "getEmployeeLifecycleDashboard");
      data.append("sessionIdNo", sessionStorage.getItem("idNo") || "");
      execute(data, authUrl).then((res) => {
        if (res.data.success && res.data.data) {
          this.dashboard = { ...this.dashboard, ...res.data.data };
        }
      });
    },
    loadPipeline() {
      const data = new FormData();
      data.append("function", "getRecruitmentPipelineForLifecycle");
      execute(data, authUrl).then((res) => {
        this.pipeline = res.data.success && Array.isArray(res.data.data)
          ? res.data.data.filter((a) => !a.employee_id)
          : [];
      });
    },
    debouncedSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.runSearch, 300);
    },
    runSearch() {
      const q = this.searchQuery.trim();
      if (q.length < 2) {
        this.searchResults = [];
        return;
      }
      const data = new FormData();
      data.append("function", "searchEmployeesForLifecycle");
      data.append("q", q);
      execute(data, authUrl).then((res) => {
        this.searchResults = res.data.success && Array.isArray(res.data.data) ? res.data.data : [];
      });
    },
    selectEmployee(e) {
      this.selectedEmployee = e;
      this.selectedApplicant = null;
    },
    selectApplicant(a) {
      this.selectedApplicant = a;
      this.selectedEmployee = null;
    },
    openEmployeeProfile() {
      if (!this.selectedEmployee) return;
      sessionStorage.setItem("employeeId", this.selectedEmployee.id);
      this.$router.push("/employee-details-view");
    }
  }
};
</script>

<style scoped>
.lifecycle-search-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 280px;
  overflow-y: auto;
}
.lifecycle-search-list.compact {
  max-height: 200px;
}
.lifecycle-search-list li {
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid transparent;
}
.lifecycle-search-list li:hover,
.lifecycle-search-list li.active {
  background: #f1f5f9;
  border-color: #e2e8f0;
}
</style>
